/**
 * `<SettingsWorkspaceRoute>` — `/w/:slug/workspace`
 *
 * Tabbed workspace settings:
 *  - `?tab=general` (default) — rename the workspace (gated `update Workspace`).
 *  - `?tab=helpdesk` — helpdesk/handoff settings (gated `configure HandoffEvent`),
 *    so a support_lead who can't edit the workspace still reaches it.
 *
 * The slug is immutable post-creation per FD R0. Each tab renders only when its
 * ability holds; the page shows a no-permission panel when neither does.
 */

import {
  ME_QUERY_KEY,
  authClient,
  useAbility,
  useWorkspace,
  type AuthClientError,
} from "@cometchat-ai/auth/client";
import { updateWorkspaceSchema, type UpdateWorkspaceDto } from "@cometchat-ai/auth/shared";
import {
  Alert,
  AlertDescription,
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Loader2 } from "../components/icons";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { PageShell } from "../components/page-shell";
import { SettingsCard } from "../components/settings-card";
import { WorkspaceIcon } from "../components/workspace-icon";
import { HelpdeskSettingsPanel } from "../components/helpdesk-settings-panel";
import { formatApiError } from "../lib/format-error";

const INPUT_CLASS = "h-9 rounded-lg text-[13px] placeholder:text-[color:var(--color-fg-subtle)]";

type TabId = "general" | "helpdesk";

export function SettingsWorkspaceRoute() {
  const { t } = useTranslation("workspace");
  const ability = useAbility();
  const canUpdate = ability.can("update", "Workspace");
  const canHelpdesk = ability.can("configure", "HandoffEvent");

  const [searchParams, setSearchParams] = useSearchParams();
  const tabs: Array<{ value: TabId; label: string }> = [
    ...(canUpdate
      ? [{ value: "general" as const, label: t("workspaceSettings.tabs.general") }]
      : []),
    ...(canHelpdesk
      ? [{ value: "helpdesk" as const, label: t("workspaceSettings.tabs.helpdesk") }]
      : []),
  ];
  const requested = searchParams.get("tab") as TabId | null;
  const activeTab: TabId =
    requested && tabs.some((x) => x.value === requested)
      ? requested
      : (tabs[0]?.value ?? "general");

  if (tabs.length === 0) {
    return (
      <PageShell className="p-0" data-testid="settings-workspace">
        <div className="mx-auto w-full max-w-3xl px-6 py-6">
          <Alert data-testid="settings-workspace-no-permission">
            <AlertDescription>{t("workspaceSettings.noPermission")}</AlertDescription>
          </Alert>
        </div>
      </PageShell>
    );
  }

  // Storybook `_settings.tsx` page layout: a centered max-w-6xl column with the
  // tab strip on top and the tab's content filling the rest.
  return (
    <PageShell className="p-0" data-testid="settings-workspace">
      <div className="flex h-full min-h-0 flex-col px-6 pt-[18px]">
        <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col">
          {tabs.length > 1 ? (
            <div className="shrink-0 pb-3">
              <div
                className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted p-0.5"
                role="tablist"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.value}
                    data-active={activeTab === tab.value}
                    data-testid={`settings-workspace-tab-${tab.value}`}
                    onClick={() => setSearchParams({ tab: tab.value })}
                    className="inline-flex h-6 items-center rounded-[5px] px-2.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground data-[active=true]:bg-background data-[active=true]:text-foreground data-[active=true]:shadow-sm"
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div
            className={
              activeTab === "helpdesk"
                ? "min-h-0 flex-1 overflow-hidden pb-6"
                : "min-h-0 flex-1 overflow-auto pb-6"
            }
          >
            {activeTab === "helpdesk" ? <HelpdeskSettingsPanel /> : <WorkspaceGeneralPanel />}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

/** The workspace rename form (the original General tab content). */
function WorkspaceGeneralPanel() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const queryClient = useQueryClient();

  const form = useForm<UpdateWorkspaceDto>({
    resolver: zodResolver(updateWorkspaceSchema),
    defaultValues: { name: workspace.name, logoUrl: workspace.logoUrl ?? "" },
  });

  const mutation = useMutation({
    mutationFn: (values: UpdateWorkspaceDto) => authClient.updateWorkspace(workspace.slug, values),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      form.reset({ name: updated.name, logoUrl: updated.logoUrl ?? "" });
      toast.success(t("workspaceSettings.savedToast"));
    },
    onError: (err: AuthClientError) => {
      toast.error(t("workspaceSettings.updateFailed"), { description: formatApiError(err) });
    },
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-6">
      <Form {...form}>
        <form
          id="settings-workspace-form"
          onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
          data-testid="settings-workspace-form"
          noValidate
        >
          <SettingsCard icon={Building2} title={t("workspaceSettings.section.title")}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-foreground">
                      {t("workspaceSettings.name.label")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="organization"
                        className={INPUT_CLASS}
                        data-testid="settings-workspace-name"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs text-muted-foreground">
                      {t("workspaceSettings.name.hint")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="logoUrl"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-foreground">
                      {t("workspaceSettings.icon.label")}
                    </FormLabel>
                    <div className="flex items-center gap-3">
                      <WorkspaceIcon
                        src={field.value || null}
                        className="size-9 shrink-0 border border-border"
                      />
                      <FormControl>
                        <Input
                          type="url"
                          inputMode="url"
                          autoComplete="off"
                          placeholder={t("workspaceSettings.icon.placeholder")}
                          className={INPUT_CLASS}
                          data-testid="settings-workspace-icon"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                    </div>
                    <FormDescription className="text-xs text-muted-foreground">
                      {t("workspaceSettings.icon.hint")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-1.5">
                <Label
                  htmlFor="settings-workspace-slug"
                  className="text-[13px] font-medium text-foreground"
                >
                  {t("workspaceSettings.slug.label")}
                </Label>
                <Input
                  id="settings-workspace-slug"
                  value={workspace.slug}
                  readOnly
                  aria-readonly="true"
                  className={`${INPUT_CLASS} text-muted-foreground`}
                  data-testid="settings-workspace-slug"
                />
                <p className="text-xs text-muted-foreground">{t("workspaceSettings.slug.hint")}</p>
              </div>

              <div className="flex justify-end border-t border-border pt-4">
                <Button
                  type="submit"
                  size="sm"
                  className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
                  disabled={mutation.isPending || !form.formState.isDirty}
                  data-testid="settings-workspace-submit"
                >
                  {mutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {mutation.isPending
                    ? t("workspaceSettings.submitting")
                    : t("workspaceSettings.submit")}
                </Button>
              </div>
            </div>
          </SettingsCard>
        </form>
      </Form>
    </div>
  );
}
